import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AUTH_SERVER_URL, OFFER_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { AuctionOffer, AUCTION_OFFER_FIELD, FieldOfAuctionOffer, Bid, FieldOfBid } from '../models/auction-offer';
import { AuctionSettings } from '../models/auction-settings';
import { TransportPrice } from '../models/transport-price';
import { OrderBy } from '../../shared/utils/constant';
import { ProductFamilyTonnage } from '../models/offer-family';
import { MillTonnage } from '../models/mill';

const GET_AUCTION_OFFERS = `${ OFFER_SERVER_URL }/v1`;
const GET_AUCTION_SETTINGS = `${ AUTH_SERVER_URL }/v1`;

export const AUCTION_SERVICE = 'PAUCT';

@Injectable({ providedIn: 'root' })
export class AuctionOfferService {
	auctionOffer = new AuctionOffer();

	constructor(private readonly http: HttpClient) {}

	getAuctionOffers(
		fields?: readonly FieldOfAuctionOffer[],
		filter?: string,
		index?: number,
		limit?: number,
		orderBy?: OrderBy<keyof AuctionOffer>[] | OrderBy<keyof AuctionOffer>,
		distinct?: boolean,
		onlyCount?: boolean,
	) {
		const url = new URL(`${ GET_AUCTION_OFFERS }/auctionOffers`);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		// Filters
		if (filter) {
			params.append('filter', filter);
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (index !== undefined && limit !== undefined) {
			params.append('page', String(Math.floor(index / limit) + 1));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (distinct) {
			params.append('distinct', String(distinct));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		return this.http.get<PaginatedResult<AuctionOffer>>(url.toString());
	}

	getAuctionOfferById(offerId: number) {
		const fields = AUCTION_OFFER_FIELD;
		const url = new URL(`${ GET_AUCTION_OFFERS }/auctionOffers/${ offerId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));

		return this.http.get<AuctionOffer>(url.toString());
	}

	getAuctionSettings(
		fields: 'soldtos',
		index: number,
		limit: number,
		orderBy: OrderBy<keyof AuctionSettings>[] | OrderBy<keyof AuctionSettings>,
		onlyCount: boolean,
		onlyDistinct: boolean
	) {
		const url = new URL(`${ GET_AUCTION_SETTINGS }/auctionSettings`);
		const params = url.searchParams;
		// Fields
		params.append('fields', fields);
		// Orderby
		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));
		params.append('onlyCount', String(onlyCount));
		params.append('distinct', String(onlyDistinct));
		return this.http.get<PaginatedResult<AuctionSettings>>(url.toString());
	}

	getTransportCost(date: string, incoterm: string, millsyst: string, modeoftransport: string, shipto: string) {
		const url = new URL(`${ GET_AUCTION_OFFERS }/transportCost`);
		const params = url.searchParams;

		params.append('date', date);
		params.append('incoterm', incoterm);
		params.append('millSyst', millsyst);
		params.append('modeOfTransport', modeoftransport);
		params.append('shipTo', shipto);
		return this.http.get<TransportPrice>(url.toString());
	}

	getBidList(
		fields?: readonly FieldOfBid[],
		filter?: string,
		index?: number,
		limit?: number,
		orderBy?: OrderBy<keyof Bid>[] | OrderBy<keyof Bid>,
		distinct?: boolean,
		onlyCount?: boolean,
	) {
		const url = new URL(`${ GET_AUCTION_OFFERS }/auctionBids`);
		const params = url.searchParams;
		if (fields) {
			params.append('fields', fields.join(','));
		}
		// Filters
		if (filter) {
			params.append('filter', filter);
		}
		if (orderBy) {
			params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));
		}
		if (index !== undefined && limit !== undefined) {
			params.append('page', String(Math.floor(index / limit) + 1));
		}
		if (limit !== undefined) {
			params.append('limit', String(limit));
		}
		if (distinct) {
			params.append('distinct', String(distinct));
		}
		if (onlyCount) {
			params.append('onlyCount', String(onlyCount));
		}
		return this.http.get<PaginatedResult<Bid>>(url.toString());
	}

	async saveBids() {
		const url = new URL(`${ GET_AUCTION_OFFERS }/saveBids`);
		const headers = new HttpHeaders().set('Content-Type', 'application/json');
		await firstValueFrom(this.http.post<PaginatedResult<Bid>>(url.toString(), { headers }));
	}

	getCountOfOpenedAuctionOffers() {
		const url = new URL(`${ GET_AUCTION_OFFERS }/auctionOffers:countByOpenStatus`);
		return this.http.get<PaginatedResult<number>>(url.toString());
	}

	getSumOfTonnageOfOpenedAuctionOffers() {
		const url = new URL(`${ GET_AUCTION_OFFERS }/auctionOffers:sumTonnageByOpenStatus`);
		return this.http.get<number>(url.toString());
	}

	getAvailableFamiliesAndTonnage() {
		const url = new URL(`${ GET_AUCTION_OFFERS }/auctionOffers:aggregateByProductFamily`);
		return this.http.get<PaginatedResult<ProductFamilyTonnage>>(url.toString());
	}

	getAvailableMillsAndTonnage() {
		const url = new URL(`${ GET_AUCTION_OFFERS }/auctionOffers:aggregateByMill`);
		return this.http.get<PaginatedResult<MillTonnage>>(url.toString());
	}
}
